import { Injectable } from '@angular/core';
import { AngularFirestore, AngularFirestoreCollection } from '@angular/fire/compat/firestore';
import { Observable, Subject, from, of, switchMap, zip, map, combineLatest } from 'rxjs';
import { ChatEntity } from 'src/app/models/entities/chat.entity';
import { BoardService } from './board.service';
import { UserEntity } from 'src/app/models/entities/user.entity';
import { TenantService } from '../../services/tenant.service';
import { UserService } from './user.service';
import firebase from 'firebase/compat/app';

@Injectable({
  providedIn: 'root',
})
export class ChatService {
  private tenantId: string = '';
  private newPath: string = '';
  public chatRead$: Subject<any> = new Subject();
  private selectedUserSubject = new Subject<UserEntity | null>();
  selectedUser$ = this.selectedUserSubject.asObservable();

  constructor(
    private afs: AngularFirestore,
    private boardService: BoardService,
    private tenantService: TenantService,
    private userService: UserService // Inject the UserService
  ) {
    this.tenantService.tenantId$.subscribe(tenantId => {
      if (tenantId) {
        this.tenantId = tenantId;
        this.newPath = `${this.tenantId}/data/messages`;
      }
    });
  }

  setSelectedUser(user: UserEntity) {
    this.selectedUserSubject.next(user);
  }

  public save(projectKey: string, cardKey: string, chat: ChatEntity): Observable<string> {
    let result$: Subject<string> = new Subject();

    chat.createdAt = new Date();
    chat.card = cardKey;
    chat.boardKey = projectKey; // Add boardKey to chat
    chat.recipients = chat.recipients || [];
   
    
    
    this.afs.collection(this.newPath).add(chat).then(docRef => {
      
      chat.key = docRef.id;
      result$.next(docRef.id);
      result$.complete(); // Ensure the observable completes
    }).catch(error => {
      console.error('Error saving chat:', error);
      result$.error(error);
    });

    return result$.asObservable();
  }
  sendMessage(user: UserEntity, message: string): Observable<void> {
    const chat: ChatEntity = {
      key: '',
      card: '',
      cardKey: '',
      boardKey: '',
      cardName: '',
      boardName: '',
      createdAt: new Date(),
      updatedAt: new Date(),
      message,
      senderName: user.name,
      senderKey: user.key,
      replyToSenderKey: '',
      file: '',
      read: false,
      replyTo: null,
      recipients: [],
      cardsMentioned: [],
    };
    return from(this.afs.collection('chats').add(chat)).pipe(map(() => { }));
  }
  public update(projectKey: string, chatKey: string, chat: ChatEntity): Promise<void> {
    const chatDocPath = `${this.newPath}/${chatKey}`;
    return this.afs.doc(chatDocPath).update(chat);
  }

  public set(projectKey: string, chatKey: string, chat: ChatEntity): Promise<void> {
    const chatDocPath = `${this.newPath}/${chatKey}`;
    return this.afs.doc(chatDocPath).set(chat);
  }

  public delete(projectKey: string, cardKey: string, chatKey: string): Promise<void> {
    const chatDocPath = `${this.newPath}/${chatKey}`;
    return this.afs.doc(chatDocPath).delete();
  }

  public getItems(projectKey: string, cardKey: string): AngularFirestoreCollection<ChatEntity> {
    return this.afs.collection<ChatEntity>(this.newPath, ref =>
      ref.where('card', '==', cardKey)
        .where('boardKey', '==', projectKey)
        .orderBy('createdAt', 'desc')
    );
  }
  public getChatsForUser(userKey: string): Observable<ChatEntity[]> {
    return this.afs.collection<ChatEntity>(this.newPath, ref =>
      ref.where('recipients', 'array-contains', userKey)
        .orderBy('createdAt', 'desc')
    ).valueChanges({ idField: 'key' }).pipe(
      map(chats => {
        // Remove duplicates based on chat key
        const uniqueChats = chats.filter((chat, index, self) =>
          index === self.findIndex((c) => c.key === chat.key)
        );

        // Sort by createdAt in descending order
        uniqueChats.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

        return uniqueChats;
      })
    );
  }
  public getChatsMentioningUser(userKey: string): Observable<ChatEntity[]> {
    return this.afs.collection<ChatEntity>(this.newPath, ref =>
      ref.where('recipients', 'array-contains', userKey)
        .orderBy('createdAt', 'desc')
    ).valueChanges({ idField: 'key' });
  }
  public getChatsMentioningUserCount(currentUserId: string): Observable<number> {
    return this.afs.collection<ChatEntity>(this.newPath, ref =>
      ref.where('recipients', 'array-contains', currentUserId)
        .where('read', '==', false)
        .where('senderKey', '!=', currentUserId) // Exclude messages sent by the current user
    ).valueChanges({ idField: 'key' }).pipe(
      map(chats => {
        
        return chats.length;
      })
    );
  }
  public getAll(): AngularFirestoreCollection<ChatEntity> {
    return this.afs.collection(this.newPath, ref => ref.orderBy('createdAt'));
  }

  public getItemsProject(projectKey: string): AngularFirestoreCollection<ChatEntity> {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return this.afs.collection<ChatEntity>(this.newPath, ref =>
      ref.where('boardKey', '==', projectKey)
        .where('createdAt', '>=', today)
        .orderBy('createdAt')
    );
  }

  public getUnreadForBoard(boardKey: string, userKey: string): AngularFirestoreCollection<ChatEntity> {
    return this.afs.collection<ChatEntity>(this.newPath, ref =>
      ref.where('boardKey', '==', boardKey)
        .where('recipients', 'array-contains', userKey)
        .where('read', '==', false)
        .orderBy('createdAt', 'desc')
    );
  }

  public markAllReadForCard(projectKey: string, cardKey: string, chats: ChatEntity[]): Promise<void[]> {

    const updates = chats.map(chat => {
      const chatDocPath = `${this.newPath}/${chat.key}`;
      return this.afs.doc(chatDocPath).update({ read: true });
    });

    return Promise.all(updates);
  }

  // Method to get unread chats for a specific user across all projects
  public getUnreadChatsForUser(userId: string): Observable<ChatEntity[]> {
    return this.afs.collection<ChatEntity>(this.newPath, ref =>
      ref.where('recipients', 'array-contains', userId)
        .where('read', '==', false)
        .orderBy('createdAt')
    ).valueChanges({ idField: 'key' });
  }

  // Method to import existing chats into the new path
  public importChats(): Observable<void> {
    return this.afs.collectionGroup<ChatEntity>('chats').get().pipe(
      switchMap(snapshot => {
        const batch = this.afs.firestore.batch();
        snapshot.forEach(doc => {
          const data = doc.data();
          const chatDocPath = doc.ref.path; // e.g., tenant/{tenantId}/chats/{projectKey}/chats/{chatKey}
          const pathSegments = chatDocPath.split('/');
          const boardKey = pathSegments[3]; // Assuming {projectKey} is at the 4th position

          // Add boardKey to each chat document
          const newData = { ...data, boardKey };
          const newDocRef = this.afs.firestore.collection(this.newPath).doc(doc.id);
          batch.set(newDocRef, newData);
        });
        return from(batch.commit());
      })
    );
  }
  public migrateChatFields(users: UserEntity[]): Observable<void> {
    return this.afs.collectionGroup<any>('messages').get().pipe(
      switchMap(snapshot => {
        const batch = this.afs.firestore.batch();
        snapshot.forEach(doc => {
          const data = doc.data();
          const docRef = doc.ref;
          
          const updatedData: any = {};
  
          if (data.user !== undefined) {
            updatedData.senderName = data.user;
          }
  
          const user = users.find(user => user.name === data.user);
          if (user && user.key) {
            updatedData.senderKey = user.key;
          }
          if(data.mentioned){
            updatedData.recipients= data.mentioned;
          }else{
            updatedData.recipients=[];
          
          }
  
          if (Object.keys(updatedData).length > 0) {
            batch.update(docRef, updatedData);
          }
        });
        return from(batch.commit());
      })
    );
  }

  public markAsRead(messageKeys: string[]): Observable<void> {
    const batch = this.afs.firestore.batch();
    
    messageKeys.forEach(key => {
      const ref = this.afs.collection(this.newPath).doc(key).ref;
      batch.update(ref, { read: true });
    });

    return from(batch.commit()).pipe(
      map(() => {
        this.chatRead$.next(messageKeys);
      })
    );
  }

  public getHistoricalChats(
    userKey: string, 
    limit: number = 10, 
    beforeTimestamp?: any, 
    selectedUserKey?: string,
    boardKey?: string
  ): Observable<ChatEntity[]> {
    let query = (ref: firebase.firestore.CollectionReference) => {
      let baseQuery = ref
        .where('recipients', 'array-contains', userKey)
        .orderBy('createdAt', 'desc')
        .limit(limit);

      if (beforeTimestamp) {
        // Convert to proper Firestore timestamp
        let timestamp;
        
        if (beforeTimestamp instanceof firebase.firestore.Timestamp) {
          timestamp = beforeTimestamp;
        } else if (beforeTimestamp instanceof Date) {
          timestamp = firebase.firestore.Timestamp.fromDate(beforeTimestamp);
        } else if (typeof beforeTimestamp.toDate === 'function') {
          // Handle cases where it might be a Firestore timestamp-like object
          timestamp = firebase.firestore.Timestamp.fromDate(beforeTimestamp.toDate());
        } else {
          // Try to make a date from whatever this is
          try {
            const date = new Date(beforeTimestamp);
            timestamp = firebase.firestore.Timestamp.fromDate(date);
          } catch (e) {
            // Default to now minus 1 day to get some recent messages
            timestamp = firebase.firestore.Timestamp.fromDate(
              new Date(Date.now() - 86400000) // 24 hours ago
            );
          }
        }
        
        baseQuery = baseQuery.startAfter(timestamp);
      }

      return baseQuery;
    };

    return this.afs.collection<ChatEntity>(this.newPath, query)
      .valueChanges({ idField: 'key' })
      .pipe(
        map(chats => {
          // Filter by selected user if provided
          if (selectedUserKey) {
            const beforeFilter = chats.length;
            chats = chats.filter(chat => 
              (chat.senderKey === userKey && chat.recipients.includes(selectedUserKey)) ||
              (chat.senderKey === selectedUserKey && chat.recipients.includes(userKey)) ||
              (chat.senderKey === selectedUserKey && chat.replyToSenderKey === userKey) ||
              (chat.senderKey === userKey && chat.replyToSenderKey === selectedUserKey) ||
              (chat.recipients.includes(userKey) && chat.recipients.includes(selectedUserKey))
            );
          }
          
          // Filter by board if provided
          if (boardKey) {
            const beforeFilter = chats.length;
            chats = chats.filter(chat => chat.boardKey === boardKey);
          }
          
          return chats;
        })
      );
  }

  public getRealtimeChats(userKey: string, afterTimestamp: any): Observable<ChatEntity[]> {
    // If it's already a Timestamp, use it directly
    const timestamp = afterTimestamp instanceof firebase.firestore.Timestamp
      ? afterTimestamp
      : firebase.firestore.Timestamp.fromDate(afterTimestamp);

    return this.afs.collection<ChatEntity>(this.newPath, ref =>
      ref.where('recipients', 'array-contains', userKey)
         .where('createdAt', '>', timestamp)
         .orderBy('createdAt', 'desc')
         .limit(50) // Limit real-time updates to last 50 messages
    ).valueChanges({ idField: 'key' });
  }

  /**
   * Get paginated chats for embedded mode (card-specific chats)
   */
  public getEmbeddedChats(
    boardKey: string,
    cardKey: string,
    limit: number = 20
  ): Observable<ChatEntity[]> {
    return this.afs.collection<ChatEntity>(this.newPath, ref =>
      ref.where('card', '==', cardKey)
         .where('boardKey', '==', boardKey)
         .orderBy('createdAt', 'desc')
         .limit(limit)
    ).valueChanges({ idField: 'key' }).pipe(
      map(chats => {
        // Return in chronological order (oldest first)
        return chats.reverse();
      })
    );
  }

  /**
   * Get real-time embedded chats (new messages only)
   */
  public getRealtimeEmbeddedChats(
    boardKey: string,
    cardKey: string,
    afterTimestamp: any
  ): Observable<ChatEntity[]> {
    const timestamp = afterTimestamp instanceof firebase.firestore.Timestamp
      ? afterTimestamp
      : firebase.firestore.Timestamp.fromDate(afterTimestamp);

    return this.afs.collection<ChatEntity>(this.newPath, ref =>
      ref.where('card', '==', cardKey)
         .where('boardKey', '==', boardKey)
         .where('createdAt', '>', timestamp)
         .orderBy('createdAt', 'asc')
         .limit(50) // Limit real-time updates
    ).valueChanges({ idField: 'key' });
  }

  /**
   * Get older embedded chats before a specific timestamp (for pagination)
   */
  public getEmbeddedChatsBeforeTimestamp(
    boardKey: string,
    cardKey: string,
    limit: number,
    beforeTimestamp: any
  ): Observable<ChatEntity[]> {
    let timestamp;

    if (beforeTimestamp instanceof firebase.firestore.Timestamp) {
      timestamp = beforeTimestamp;
    } else if (beforeTimestamp instanceof Date) {
      timestamp = firebase.firestore.Timestamp.fromDate(beforeTimestamp);
    } else if (typeof beforeTimestamp.toDate === 'function') {
      timestamp = firebase.firestore.Timestamp.fromDate(beforeTimestamp.toDate());
    } else {
      try {
        const date = new Date(beforeTimestamp);
        timestamp = firebase.firestore.Timestamp.fromDate(date);
      } catch (e) {
        timestamp = firebase.firestore.Timestamp.fromDate(
          new Date(Date.now() - 86400000)
        );
      }
    }

    return this.afs.collection<ChatEntity>(this.newPath, ref =>
      ref.where('card', '==', cardKey)
         .where('boardKey', '==', boardKey)
         .where('createdAt', '<', timestamp)
         .orderBy('createdAt', 'desc')
         .limit(limit)
    ).valueChanges({ idField: 'key' }).pipe(
      map(chats => {
        // Return in chronological order (oldest first)
        return chats.reverse();
      })
    );
  }
}